import "./SaleCorePropertiesManager.scss";

import { Box, Button, Divider, Grid, IconButton, Typography } from "@material-ui/core";
import AddIcon from '@material-ui/icons/Add';
import AutorenewIcon from "@material-ui/icons/Autorenew";
import RemoveIcon from '@material-ui/icons/Remove';
import React, { Fragment } from "react";
import { useDrag, useDrop } from 'react-dnd'
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { Category } from "../../backend/generated/category-model";
import { CreatePropertyInput } from "../../backend/generated/create-property-input";
import { SaleCoreProperties } from "../../backend/generated/create-sale-input";
import { PropertyType } from "../../backend/types/utils";
import { CreatePropertyManageable } from "../../store/property/types";
import { CreateSaleManageable, SaleManageable } from "../../store/sale/types";
import { DraggableItemType } from "../../utils/drag";
import { Mode } from "../../utils/utility";
import { ActionButton } from "../ActionButton";
import { FilledTextFieldProps, OutlinedTextFieldProps, StandardTextFieldProps, TextField } from "../input/elements/TextField";
import { SaleField, SaleInputTypeKey } from "../input/fields";
import { getLabel, getPlaceholder } from "../input/fields/SaleField";
import { SubformProps } from "../SubformProps";

interface CommonCorePropertyProps extends SubformProps<CreateSaleManageable | SaleManageable> {
  id: SaleInputTypeKey;
  mode: Mode;
}

const CommonCoreProperty = (props: CommonCorePropertyProps) => {
  const { mode } = props;

  const placeholder = getPlaceholder(
    props.id,
    false
  );
  const label = getLabel(
    props.id,
    false
  );
  const coreLabel = label || placeholder;

  return (
    <Fragment>
      <Grid item xs={7} className="grid-container">
        <Typography variant="body1" color="primary" className="item-text">
          {coreLabel}:
        </Typography>
      </Grid>

      <Grid item xs={5}>
        <SaleField
          {...props}
          noLabels
          className={mode === Mode.Edit ? "" : "item-value"}
          variant="filled"
        />
      </Grid>
    </Fragment>
  );
}

type CustomCorePropertyError = {
  [key in keyof CreatePropertyInput]: boolean;
}

interface BaseCustomCorePropertyProps {
  error: CustomCorePropertyError | undefined;
  onChange: (value: CreatePropertyInput) => void;
  value: CreatePropertyInput;
}

type Omitted = 'error' | 'onChange' | 'value';

export interface StandardCustomCorePropertyProps extends Omit<StandardTextFieldProps, Omitted>, BaseCustomCorePropertyProps { }
export interface FilledCustomCorePropertyProps extends Omit<FilledTextFieldProps, Omitted>, BaseCustomCorePropertyProps { }
export interface OutlinedSCustomCorePropertyProps extends Omit<OutlinedTextFieldProps, Omitted>, BaseCustomCorePropertyProps { }

export type CustomCorePropertyProps = StandardCustomCorePropertyProps | FilledCustomCorePropertyProps | OutlinedSCustomCorePropertyProps;

const CustomCoreProperty = (props: CustomCorePropertyProps) => {
  const {
    error,
    mode,
    onChange,
    value,
  } = props;

  const { t } = useTranslation();

  return (
    <Fragment>
      <Grid item xs={7} className="grid-container">
        <TextField
          { ...props }
          className={mode === Mode.Edit ? "" : "item-text"}
          placeholder={t("Key")}
          value={value.key}
          error={error?.key}
          onChange={(e: any) => {
            if (onChange) {
              value.key = e.target.value;
              onChange(value);
            }
          }}
        />
      </Grid>

      <Grid item xs={5}>
        <TextField
          { ...props }
          className={mode === Mode.Edit ? "" : "item-value"}
          placeholder={t("Value")}
          value={value.value}
          error={error?.value}
          onChange={(e: any) => {
            if (onChange) {
              value.value = e.target.value;
              onChange(value);
            }
          }}
        />
      </Grid>
    </Fragment>
  );
}

interface DraggableItem {
  categoryIndex: number;
  propertyIndex: number;
}

interface CorePropertyProps extends SubformProps<CreateSaleManageable | SaleManageable> {
  categoryIndex: number;
  propertyIndex: number;
  id: string;
  mode: Mode;
}

const CoreProperty = (props: CorePropertyProps) => {
  // Props
  const {
    categoryIndex,
    errors,
    id,
    mode,
    propertyIndex,
    setValues,
    values,
  } = props;

  // Hooks
  const [{ opacity }, drag] = useDrag(
    () => ({
      type: DraggableItemType.CoreProperty,
      item: {
        categoryIndex,
        propertyIndex,
      },
      collect: (monitor) => ({ opacity: monitor.isDragging() ? 0.4 : 1 }),
    }),
    [id],
  )

  // Options
  const isCoreProperty = SaleCoreProperties.includes(id);

  const customPropertyIndex = values.properties?.findIndex((property) => property.key === id);
  const customProperty = customPropertyIndex !== undefined ? values.properties?.[customPropertyIndex] : null;

  const removeProperty = () => {
    if (customPropertyIndex !== undefined) {
      values.properties?.splice(
        customPropertyIndex,
        1
      );
      values.corePropertyRepresentations[categoryIndex].properties.splice(
        propertyIndex,
        1
      );

      if (values.corePropertyRepresentations[categoryIndex].properties.length === 0) {
        values.corePropertyRepresentations.splice(
          categoryIndex,
          1
        );
      }

      setValues(values);
    }
  }

  return (
    <Grid ref={drag} container spacing={1} direction="row" style={{ opacity }} className="core-property">
      {isCoreProperty &&
      <CommonCoreProperty
        {...props}
        id={id as SaleInputTypeKey}
      />
      || customPropertyIndex !== undefined && customProperty &&
      <React.Fragment>
        <CustomCoreProperty
          variant="filled"
          mode={mode}
          value={customProperty}
          error={errors.properties?.[customPropertyIndex]}
          onChange={(property: CreatePropertyInput) => {
            if (values.properties?.[customPropertyIndex]) {
              values.properties[customPropertyIndex] = new CreatePropertyManageable(property);
              values.corePropertyRepresentations[categoryIndex].properties[propertyIndex] = property.key;
              setValues(values);
            }
          }}
        />

        {mode === Mode.Edit &&
        <ActionButton
          className="remove-property-button"
          onClick={removeProperty}
          icon={<RemoveIcon />}
        />
        }
      </React.Fragment>
      }
    </Grid>
  );
}

interface CategoryProps extends SubformProps<CreateSaleManageable | SaleManageable> {
  categoryIndex: number;
  category: Category;
  mode: Mode;
}

const CategoryBox = (props: CategoryProps) => {
  // Props
  const {
    category,
    categoryIndex,
    mode,
    setFieldValue,
    setValues,
    values,
  } = props;

  // Hooks
  const [{
    isOver, canDrop
  }, drop] = useDrop({
    accept: [DraggableItemType.CoreProperty],
    drop: (item: DraggableItem) => {
      const oldCategoryIndex = item.categoryIndex;
      const oldPropertyIndex = item.propertyIndex;

      let oldCategory = values.corePropertyRepresentations[oldCategoryIndex].properties;
      let property = oldCategory[oldPropertyIndex];
      oldCategory.splice(
        oldPropertyIndex,
        1
      );

      let newCategory = values.corePropertyRepresentations[categoryIndex].properties;
      newCategory.push(property);

      if (oldCategory.length === 0) {
        values.corePropertyRepresentations.splice(
          oldCategoryIndex,
          1
        );
      }

      setFieldValue(
        "corePropertyRepresentations",
        values.corePropertyRepresentations
      );
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  })

  // Options
  const addProperty = () => {
    if (!values.properties) {
      values.properties = [];
    }

    let property = new CreatePropertyManageable();
    property.type = PropertyType.CORE_PROPERTY;

    values.properties.push(property);

    values.corePropertyRepresentations[categoryIndex].properties.push(property.key);

    setValues(values);
  }

  const isActive = isOver && canDrop
  let backgroundColor = undefined;
  if (isActive) {
    backgroundColor = '#58C6B044'
  } else if (canDrop) {
    backgroundColor = '#0059e244';
  }

  return (
    <Grid ref={drop} container direction="row" item xs={12}>
      <Grid container item xs={12} style={{ backgroundColor }} className="category">
        {category.properties.map((
          field, i
        ) =>
          <CoreProperty
            {...props}
            categoryIndex={categoryIndex}
            propertyIndex={i}
            id={field}
            key={i}
          />)}
      </Grid>

      {mode === Mode.Edit &&
      <Grid item xs={12}>
        <Box marginTop="45px">
          <ActionButton
            className="icon-button--centered"
            onClick={addProperty}
            icon={<AddIcon />}
          />
        </Box>
      </Grid>
      }
    </Grid>
  );
}

interface SaleCorePropertiesManagerProps extends SubformProps<CreateSaleManageable | SaleManageable> {
  className?: string;
  mode: Mode;
  onAutocomplete?: () => void;
}

export const SaleCorePropertiesManager = (props: SaleCorePropertiesManagerProps) => {
  const {
    className,
    mode,
    values,
    onAutocomplete,
    setFieldValue,
  } = props;

  const childProps = { ...props } as any;
  delete childProps.onAutocomplete;

  const history = useHistory();
  const { t } = useTranslation();

  const onPurchaseShares = () => {
    history.push(`/shares/${values instanceof SaleManageable ? values.id : ''}`);
  };

  const onAddCategory = () => {
    values.corePropertyRepresentations.push({ properties: [] });
    setFieldValue(
      "corePropertyRepresentations",
      values.corePropertyRepresentations
    );
  };

  const onButtonClick = mode === Mode.View ? onPurchaseShares : onAddCategory;

  return (
    <Box className={className}>
      <Grid container direction="column" spacing={4} item>
        {mode === Mode.Edit &&
        <Grid item xs={12}>
          <IconButton
            className="secondary--small autofill"
            onClick={onAutocomplete}
          >
            <AutorenewIcon />
          </IconButton>
        </Grid>
        }

        <Grid container direction="row" spacing={2} item>
          {values.corePropertyRepresentations.map((
            category, i
          ) =>
            <Grid container direction="row" spacing={0} item key={i}>
              <CategoryBox
                {...childProps}
                category={category}
                categoryIndex={i}
              />

              {i !== values.corePropertyRepresentations.length - 1 &&
              <Grid item xs={12} className="divider">
                <Divider />
              </Grid>
              }
            </Grid>)}
        </Grid>

        <Grid item xs={12}>
          <Button
            className={`${mode === Mode.View ? "quinary" : "primary"} action`}
            variant="contained"
            color="primary"
            fullWidth
            onClick={onButtonClick}
          >
            {mode === Mode.View ? t("Purchase tokens") : t("Add category")}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

SaleCorePropertiesManager.defaultProps = {
  mode: Mode.View,
  errors: {},
  touched: {},
  setFieldValue: null,
  setValues: null
};
